import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, exportError, formatApiError } from "../lib/api";
import { useSettings, formatMoney } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { tr, useT } from "../lib/i18n";
import { LotBoard, GROUP, GROUPS } from "../components/dashboard/LotBoard";
import { TodayList } from "../components/dashboard/TodayList";
import { MonthNumbers, TargetLine } from "../components/dashboard/MonthNumbers";

const STOCK = ["Available", "Consignment", "Reserved", "On Test Drive", "Out for Inspection", "Under Preparation", "Under Maintenance"];
const today = () => new Date().toISOString().slice(0, 10);

async function allOf(status) {
  const get = (page) => api.get("/vehicles", { params: { status, limit: 200, page, sort: "stock_number" } }).then((r) => r.data);
  const first = await get(1);
  const pages = Math.ceil((first.total || 0) / 200);
  const rest = await Promise.all(Array.from({ length: Math.max(0, pages - 1) }, (_, i) => get(i + 2)));
  return [first, ...rest].flatMap((p) => p.items || []);
}

// Same merge as the server (services/body_types.py): any letter case / known alias -> master spelling, each type once.
function bodyNamer(master) {
  const m = new Map((master?.body_types || []).map((b) => [b.toLowerCase(), b]));
  Object.entries(master?.aliases || {}).forEach(([k, v]) => m.set(k, v));
  return (b) => {
    const s = String(b || "").trim();
    if (!s) return "";
    const k = s.toLowerCase();
    if (!m.has(k)) m.set(k, s);
    return m.get(k);
  };
}

async function soldThisMonth() {
  const { data } = await api.get("/dashboard/sold-this-month");
  return (data.items || []).map((v) => ({ ...v, group: "sold" }));
}

const toCar = (v) => ({ id: v.id, stock_number: v.stock_number, make: v.make, model: v.model, model_year: v.model_year, body_type: v.body_type,
  price: v.asking_price ?? v.computed?.final_selling_price, cost: v.purchase_price, group: GROUP(v.status) });

function sampleCars(n) {
  const mk = [["Toyota", "Land Cruiser", "SUV"], ["Nissan", "Patrol", "SUV"], ["Lexus", "ES", "Sedan"], ["Kia", "Sportage", "SUV"], ["Hyundai", "Elantra", "Sedan"]];
  const st = ["Available", "Available", "Available", "Reserved", "On Test Drive", "Under Preparation", "Sold"];
  return Array.from({ length: n }, (_, i) => toCar({ id: `sample-${i}`, stock_number: `S-${1000 + i}`, make: mk[i % 5][0], model: mk[i % 5][1], body_type: mk[i % 5][2],
    model_year: 2018 + (i % 7), asking_price: 6000 + ((i * 397) % 30000), purchase_price: 5000 + ((i * 311) % 25000), status: st[i % 7] }));
}

async function loadToday(user) {
  const safe = (p) => p.then((r) => r.data).catch(() => null);
  const [out, overdue, cust] = await Promise.all([
    safe(api.get("/forms/out-now")), safe(api.get("/sales/overdue")),
    safe(api.get("/customers", { params: { limit: 200, ...(user?.role === "sales" ? { assigned_to: user.id } : {}) } })),
  ]);
  const rows = [];
  (out?.items || []).forEach((f) => rows.push({ key: `f-${f.id}`, kind: f.form_type === "inspection" ? "inspection" : "drive", at: f.expected_return_at,
    overdue: f.overdue, title: f.vehicle_label, sub: f.reference, to: "/forms" }));
  (overdue?.items || []).slice(0, 8).forEach((s) => rows.push({ key: `p-${s.id}`, kind: "payment", at: null, overdue: true, title: s.buyer_name || s.reference,
    sub: tr("today.days_overdue", { n: s.days_overdue }), amount: s.outstanding ?? null, to: "/sales" }));
  (cust?.items || []).filter((c) => c.follow_up_date && c.follow_up_date.slice(0, 10) <= today() && !["Won", "Lost", "Dormant"].includes(c.stage))
    .slice(0, 10).forEach((c) => rows.push({ key: `c-${c.id}`, kind: "followup", at: c.follow_up_date.slice(0, 10) < today() ? null : c.follow_up_date,
      overdue: c.follow_up_date.slice(0, 10) < today(), title: c.full_name || c.name, sub: c.mobile || c.stage, to: `/customers/${c.id}` }));
  return rows.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
}

function useDashboard(user) {
  const [cars, setCars] = useState(null);
  const [today_, setToday] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [mine, setMine] = useState(null);
  useEffect(() => {
    const sample = Number(new URLSearchParams(window.location.search).get("lot_sample")) || 0;
    (async () => {
      try {
        if (sample) { setCars(sampleCars(Math.min(sample, 2000))); return; }
        const [stock, sold, master] = await Promise.all([Promise.all(STOCK.map(allOf)), soldThisMonth(),
          api.get("/vehicles/body-types").then((r) => r.data).catch(() => null)]);
        const name = bodyNamer(master);
        const soldIds = new Set(sold.map((c) => c.id));
        // a car with a sale this month shows once, as sold (same rule and count as "Cars sold")
        const inStock = stock.flat().map(toCar).filter((c) => !soldIds.has(c.id));
        setCars([...inStock, ...sold].map((c) => ({ ...c, body_type: name(c.body_type) })));
      } catch (e) { setCars([]); toast.error(formatApiError(e)); }
    })();
    loadToday(user).then(setToday);
    api.get("/dashboard/kpis", { params: { range: "month" } }).then(({ data }) => setKpi(data)).catch(() => setKpi({}));
    api.get("/commissions/me").then(({ data }) => setMine(data)).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return { cars, today: today_, kpi, mine };
}

function Counts({ counts, onPick, picked }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="lot-counts">
      {GROUPS.filter((g) => g !== "prep" || counts.prep).map((g) => (
        <button key={g} type="button" className="text-start" onClick={() => onPick(g)} aria-pressed={picked === g} data-testid={`lot-count-${g}`}>
          <span className={`lot-dot lot-tile ${g}`} style={{ width: 10, height: 10 }} />
          {tr(`lot.g_${g /* i18n-keys: ready,reserved,out,prep,sold */}`)} <strong className="num-display" style={{ fontSize: 18 }}>{counts[g] || 0}</strong>
        </button>
      ))}
    </div>
  );
}

function PhoneList({ cars, group, onClose }) {
  const nav = useNavigate();
  const list = cars.filter((c) => c.group === group);
  return (
    <div className="panel p-4 md:hidden ds-page" data-testid="lot-phone-list">
      <div className="flex justify-between mb-2"><strong>{tr(`lot.g_${group /* i18n-keys: ready,reserved,out,prep,sold */}`)}</strong>
        <button className="btn-ghost text-xs" onClick={onClose} data-testid="lot-phone-close">{tr("x.close")}</button></div>
      <ul>{list.slice(0, 100).map((c) => (
        <li key={c.id} className="ds-row"><button className="text-start flex-1" onClick={() => nav(`/inventory/${c.id}`)}>{c.make} {c.model} <span className="num-display">{c.stock_number}</span></button></li>
      ))}</ul>
    </div>
  );
}

export default function DashboardPage() {
  const { user, can } = useAuth();
  const canSeeMoney = ["admin", "management", "accounts"].includes(user?.role);
  const nav = useNavigate();
  const { settings } = useSettings();
  const { t } = useT();
  const { cars, today: todayRows, kpi, mine } = useDashboard(user);
  const [status, setStatus] = useState(null);
  const [body, setBody] = useState("");
  const [phonePick, setPhonePick] = useState(null);
  const counts = useMemo(() => (cars || []).reduce((m, c) => ({ ...m, [c.group]: (m[c.group] || 0) + 1 }), {}), [cars]);
  const bodies = useMemo(() => [...new Set((cars || []).map((c) => c.body_type).filter(Boolean))].sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" })), [cars]);
  const filter = useMemo(() => (status || body ? (c) => (!status || c.group === status) && (!body || c.body_type === body) : null), [status, body]);
  const sales = kpi?.sales || {};
  const monthRows = [
    { key: "sold", label: t("lot.m_sold"), value: sales.count || 0 },
    ...(canSeeMoney ? [{ key: "revenue", label: t("lot.m_revenue"), value: sales.revenue || 0, money: true },
      { key: "profit", label: t("lot.m_profit"), value: sales.gross_profit || 0, money: true }] : []),
    { key: "ready", label: t("lot.m_ready"), value: counts.ready || 0 },
  ];
  const exportExcel = async () => {
    try {
      const { data } = await api.get(`/reports/sales/excel`, { params: { range: "month" }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "sales_month.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(await exportError(e)); }
  };
  const summary = t("lot.sr_summary", { n: (cars || []).length, a: counts.ready || 0, b: counts.reserved || 0, c: counts.out || 0, d: counts.sold || 0 });

  return (
    <div className="grid gap-8 grid-cols-[minmax(0,1fr)]" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: "-0.02em" }}>{t("lot.title")}</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {can("reports", "view") && <button className="btn-secondary" onClick={exportExcel} data-testid="export-btn"><Download size={14} strokeWidth={1.75} /> {t("d2.export")}</button>}
          {can("vehicles", "create") && <button className="btn-primary" onClick={() => nav("/inventory/new")} data-testid="add-vehicle"><Plus size={14} strokeWidth={1.75} /> {t("d2.add_vehicle")}</button>}
        </div>
      </div>

      <section className="panel p-4 md:p-6 grid gap-4" data-testid="lot-section" aria-label={t("lot.board_label")}>
        <p className="sr-only" data-testid="lot-sr-summary">{summary}</p>
        <div className="hidden md:flex flex-wrap items-center gap-2">
          <button type="button" className="lot-chip" aria-pressed={!status} onClick={() => setStatus(null)} data-testid="lot-filter-all">{t("lot.all")}</button>
          {GROUPS.map((g) => <button key={g} type="button" className="lot-chip" aria-pressed={status === g} onClick={() => setStatus(status === g ? null : g)} data-testid={`lot-filter-${g}`}>{t(`lot.g_${g /* i18n-keys: ready,reserved,out,prep,sold */}`)}</button>)}
          <select className="input text-sm" style={{ marginInlineStart: "auto", width: "auto" }} value={body} onChange={(e) => setBody(e.target.value)} aria-label={t("lot.body")} data-testid="lot-filter-body">
            <option value="">{t("lot.all_bodies")}</option>
            {bodies.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="hidden md:block">
          {cars ? <LotBoard cars={cars} settings={settings} showCost={canSeeMoney} filter={filter} /> : <div className="ds-skeleton" style={{ height: 120 }} data-testid="lot-loading" />}
        </div>
        {cars && <Counts counts={counts} picked={phonePick} onPick={(g) => { setStatus(status === g ? null : g); setPhonePick(phonePick === g ? null : g); }} />}
      </section>
      {phonePick && cars && <PhoneList cars={cars} group={phonePick} onClose={() => setPhonePick(null)} />}

      <div className="grid gap-8 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <section className="panel p-4 md:p-6" data-testid="today-section">
          <h2 className="text-lg font-semibold mb-2">{t("today.title")}</h2>
          <TodayList items={todayRows || []} loading={!todayRows} settings={settings} />
        </section>
        <section className="panel p-4 md:p-6 grid gap-6 content-start" data-testid="month-section">
          <h2 className="text-lg font-semibold">{t("lot.this_month")}</h2>
          {kpi ? <MonthNumbers rows={monthRows} settings={settings} /> : <div className="ds-skeleton" style={{ height: 160 }} />}
          {mine?.target && <TargetLine target={mine.target} />}
          {mine && mine.total != null && <div className="text-sm" data-testid="commission-total-line">{t("lot.commission")}: <bdi className="num-display" style={{ fontSize: 18 }}>{formatMoney(mine.total, settings)}</bdi></div>}
        </section>
      </div>
    </div>
  );
}
